import React, { useState, useEffect } from 'react';
import stepColorsData from '../data/stepColors.json';
import englishWritingDataService from '../services/englishDataService';
import '../styles/SentenceExpansionApp.css';

const DEFAULT_STEP_LABELS = [
  'Topic', 'Add Quantity', 'Add Color', 'Add Shape', 'Add Position',
  'Add Verb', 'Add Manner', 'Add Description', 'Add Sound', 'Add Smell',
  'Add Time', 'Add Weather', 'Add Emotion', 'Add Comparison', 'Add Imagination'
];

const CompositionComposer = ({ onGenerateComposition, isGenerating, stepColors, selectedLanguage = 'english', onLoadComposition, onNewComposition }) => {
  const [compositionTitle, setCompositionTitle] = useState('');
  const [wordLength, setWordLength] = useState(300);
  const [style, setStyle] = useState('narrative');
  const [nodes, setNodes] = useState([
    { 
      id: 1, 
      name: '', 
      selectedSteps: new Set([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]),
      expandedData: null,
      isExpanding: false,
      expansionError: null,
      selectedSentenceIndices: new Set() // Set of indices of selected sentences within expandedData.steps
    }
  ]);

  // Saved compositions state
  const [savedCompositions, setSavedCompositions] = useState([]);
  const [selectedCompositionId, setSelectedCompositionId] = useState('');
  const [isLoadingCompositions, setIsLoadingCompositions] = useState(false);

  const styles = [
    { value: 'narrative', label: 'Narrative' },
    { value: 'descriptive', label: 'Descriptive' },
    { value: 'expository', label: 'Expository' },
    { value: 'persuasive', label: 'Persuasive' },
    { value: 'creative', label: 'Creative' }
  ];

  // Load saved compositions on component mount
  useEffect(() => {
    const loadSavedCompositions = async () => {
      setIsLoadingCompositions(true);
      try {
        const compositions = await englishWritingDataService.getCompositions();
        setSavedCompositions(compositions);
      } catch (error) {
        console.error('Error loading saved compositions:', error);
        // Don't show alert, just log the error
      } finally {
        setIsLoadingCompositions(false);
      }
    };

    loadSavedCompositions();
  }, []);

  // Handle loading a saved composition
  const handleLoadComposition = async (compositionId) => {
    if (!compositionId || compositionId === '') {
      return;
    }

    const composition = savedCompositions.find(c => c.id === compositionId);
    if (!composition) {
      alert('Composition not found');
      return;
    }

    try {
      // Set basic composition settings
      setCompositionTitle(composition.title);
      setWordLength(composition.wordLength || 300);
      setStyle(composition.style || 'narrative');

      // Debug: Log composition data
      console.log('[CompositionComposer] Loading composition:', {
        id: composition.id,
        title: composition.title,
        hasNodesData: !!composition.nodesData,
        nodesDataType: typeof composition.nodesData,
        nodesDataIsArray: Array.isArray(composition.nodesData),
        nodesDataLength: Array.isArray(composition.nodesData) ? composition.nodesData.length : 'N/A',
        nodesData: composition.nodesData
      });

      // Reconstruct nodes from nodesData
      if (composition.nodesData && Array.isArray(composition.nodesData) && composition.nodesData.length > 0) {
        const reconstructedNodes = composition.nodesData.map((nodeData, index) => {
          const nodeId = index + 1;
          const nodeName = nodeData.node_name || '';
          
          // Get selected sentences from saved data
          const selectedSentences = nodeData.selected_sentences || [];
          
          // Map step labels to step indices for selectedSteps
          const selectedStepIndices = new Set();
          selectedSentences.forEach(sentence => {
            const stepLabel = sentence.step_label || '';
            const stepIndex = DEFAULT_STEP_LABELS.findIndex(label => label === stepLabel);
            if (stepIndex >= 0) {
              selectedStepIndices.add(stepIndex);
            }
          });

          // Reconstruct expandedData from selected sentences
          // Since we saved the selected sentences, we use them as the expanded steps
          const expandedSteps = selectedSentences.map(sentence => ({
            label: sentence.step_label || '',
            content: sentence.sentence_content || ''
          }));

          // Find which sentences were selected (all of them, since they were the ones saved)
          const selectedSentenceIndices = new Set();
          expandedSteps.forEach((step, index) => {
            selectedSentenceIndices.add(index);
          });

          return {
            id: nodeId,
            name: nodeName,
            selectedSteps: selectedStepIndices.size > 0 ? selectedStepIndices : new Set([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]),
            expandedData: {
              topic: nodeName,
              steps: expandedSteps
            },
            isExpanding: false,
            expansionError: null,
            selectedSentenceIndices: selectedSentenceIndices
          };
        });

        setNodes(reconstructedNodes);
        
        // Notify parent component to display the final composition text
        if (onLoadComposition && composition.finalCompositionText) {
          // Reconstruct nodes array in the format expected for display
          const displayNodes = reconstructedNodes.map((node, index) => {
            const selectedSteps = Array.from(node.selectedSentenceIndices)
              .sort((a, b) => a - b)
              .map(sentenceIndex => node.expandedData.steps[sentenceIndex])
              .filter(step => step); // Remove undefined steps
            
            return {
              name: node.name,
              steps: selectedSteps.map(step => ({
                label: step.label,
                content: step.content
              }))
            };
          });

          // Create composition object for display
          const compositionForDisplay = {
            title: composition.title,
            style: composition.style,
            targetWordLength: composition.wordLength,
            nodes: displayNodes,
            finalComposition: composition.finalCompositionText
          };

          onLoadComposition(compositionForDisplay);
        }
        
        // Show success message
        alert(`Composition "${composition.title}" loaded successfully!`);
      } else {
        // More detailed error message
        const errorMsg = composition.nodesData 
          ? `Composition "${composition.title}" has invalid node data format (expected array, got ${typeof composition.nodesData})`
          : `No node data found in saved composition "${composition.title}". The composition may have been saved without nodes.`;
        console.error('[CompositionComposer] Cannot load composition:', {
          composition,
          nodesData: composition.nodesData,
          nodesDataType: typeof composition.nodesData,
          isArray: Array.isArray(composition.nodesData)
        });
        alert(errorMsg);
      }
    } catch (error) {
      console.error('Error loading composition:', error);
      alert(`Error loading composition: ${error.message}`);
    }

    // Clear the dropdown selection
    setSelectedCompositionId('');
  };

  const addNode = () => {
    const newNodeId = Math.max(...nodes.map(n => n.id), 0) + 1;
    setNodes([...nodes, {
      id: newNodeId,
      name: '',
      selectedSteps: new Set([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]),
      expandedData: null,
      isExpanding: false,
      expansionError: null,
      selectedSentenceIndices: new Set()
    }]);
  };

  const removeNode = (nodeId) => {
    if (nodes.length > 1) {
      setNodes(nodes.filter(node => node.id !== nodeId));
    }
  };

  const updateNodeName = (nodeId, name) => {
    setNodes(nodes.map(node => 
      node.id === nodeId ? { ...node, name } : node
    ));
  };

  const toggleNodeStep = (nodeId, stepIndex) => {
    setNodes(nodes.map(node => {
      if (node.id === nodeId) {
        const newSteps = new Set(node.selectedSteps);
        if (newSteps.has(stepIndex)) {
          // Don't allow removing if it's the only step selected
          if (newSteps.size > 1) {
            newSteps.delete(stepIndex);
          }
        } else {
          newSteps.add(stepIndex);
        }
        return { ...node, selectedSteps: newSteps };
      }
      return node;
    }));
  };

  // Handle individual node expansion
  const handleNodeExpand = async (nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node || !node.name.trim()) {
      alert('Please enter a name for this node');
      return;
    }

    // Set expanding state
    setNodes(nodes.map(n => 
      n.id === nodeId ? { ...n, isExpanding: true, expansionError: null } : n
    ));

    try {
      // Use A2A service for sentence expansion
      const result = await englishWritingDataService.expandSentence(
        node.name.trim(),
        {
          context: 'general',
          expansion_type: 'descriptive'
        }
      );

      // Transform A2A result to match expected format
      const expandedSentences = result.expandedSentences || result.data?.expandedSentences || [];
      const steps = expandedSentences.map(step => ({
        label: step.label || '',
        content: step.content || ''
      }));

      if (steps && steps.length > 0) {
        // Filter steps based on selected steps for this node
        // steps should be in order (0-14), so we filter by index
        // Get sorted selected step indices
        const selectedStepsArray = Array.from(node.selectedSteps).sort((a, b) => a - b);
        
        // Extract steps in the order of selected steps
        const filteredSteps = selectedStepsArray.map(stepIndex => {
          return steps[stepIndex];
        }).filter(step => step !== undefined); // Safety check

        // Update node with expanded data
        setNodes(nodes.map(n => 
          n.id === nodeId ? { 
            ...n, 
            expandedData: {
              topic: node.name.trim(),
              steps: filteredSteps
            },
            isExpanding: false,
            expansionError: null,
            selectedSentenceIndices: new Set() // Reset selection when new expansion is generated
          } : n
        ));
      } else {
        throw new Error('No steps returned from expansion');
      }
    } catch (error) {
      console.error('Node expansion error:', error);
      setNodes(nodes.map(n => 
        n.id === nodeId ? { 
          ...n, 
          isExpanding: false,
          expansionError: error.message 
        } : n
      ));
    }
  };

  // Handle sentence selection for a node (toggle selection)
  const handleSentenceSelect = (nodeId, stepIndex) => {
    setNodes(nodes.map(node => {
      if (node.id === nodeId) {
        const newSelectedIndices = new Set(node.selectedSentenceIndices);
        if (newSelectedIndices.has(stepIndex)) {
          newSelectedIndices.delete(stepIndex);
        } else {
          newSelectedIndices.add(stepIndex);
        }
        return { ...node, selectedSentenceIndices: newSelectedIndices };
      }
      return node;
    }));
  };

  // Handle final composition compilation
  const handleCompose = () => {
    // Validate inputs
    if (!compositionTitle.trim()) {
      alert('Please enter a composition title');
      return;
    }

    // Get nodes that have expanded data and at least one selected sentence
    const validNodes = nodes.filter(node => 
      node.name.trim() !== '' && 
      node.expandedData !== null && 
      node.selectedSentenceIndices.size > 0 &&
      Array.from(node.selectedSentenceIndices).every(index => 
        index >= 0 && index < node.expandedData.steps.length
      )
    );

    if (validNodes.length === 0) {
      alert('Please select at least one sentence from the expanded nodes');
      return;
    }

    // Send only the selected sentences from each node
    const compositionData = {
      title: compositionTitle.trim(),
      wordLength: parseInt(wordLength) || 300,
      style,
      nodes: validNodes.map(node => {
        const selectedSteps = Array.from(node.selectedSentenceIndices)
          .sort((a, b) => a - b)
          .map(index => node.expandedData.steps[index]);
        return {
          name: node.name.trim(),
          expandedSteps: selectedSteps // Send all selected sentences for this node
        };
      })
    };

    console.log('Composition data to compile:', compositionData);

    onGenerateComposition(compositionData);
  };

  // Check if at least one sentence is selected from at least one expanded node
  const hasSelectedSentences = nodes.some(node => 
    node.name.trim() !== '' && 
    node.expandedData !== null && 
    node.selectedSentenceIndices.size > 0 &&
    Array.from(node.selectedSentenceIndices).every(index => 
      index >= 0 && index < node.expandedData.steps.length
    )
  );

  // Count total selected sentences across all nodes
  const totalSelectedSentences = nodes.reduce((total, node) => {
    if (node.expandedData && node.selectedSentenceIndices.size > 0) {
      return total + node.selectedSentenceIndices.size;
    }
    return total;
  }, 0);

  // Handle starting a new composition
  const handleNewComposition = () => {
    // Reset all form fields
    setCompositionTitle('');
    setWordLength(300);
    setStyle('narrative');
    
    // Reset nodes to default state (single empty node)
    setNodes([
      { 
        id: 1, 
        name: '', 
        selectedSteps: new Set([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14]),
        expandedData: null,
        isExpanding: false,
        expansionError: null,
        selectedSentenceIndices: new Set()
      }
    ]);
    
    // Clear dropdown selection
    setSelectedCompositionId('');
    
    // Clear composition display in parent component
    if (onLoadComposition) {
      onLoadComposition(null);
    }
    
    // Notify parent to clear composition state
    if (onNewComposition) {
      onNewComposition();
    }
  };

  return (
    <div className="composition-composer" style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: '#333' }}>📝 Composition Composer</h2>
        <button
          onClick={handleNewComposition}
          style={{
            padding: '8px 16px',
            backgroundColor: '#17a2b8',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          ✨ New Composition
        </button>
      </div>
      
      {/* Load Saved Composition Dropdown */}
      <div className="load-composition-section" style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #ddd' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#555', fontSize: '14px' }}>
          📚 Load Saved Composition:
        </label>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select
            value={selectedCompositionId}
            onChange={(e) => {
              setSelectedCompositionId(e.target.value);
              if (e.target.value) {
                handleLoadComposition(e.target.value);
              } else {
                // If user clears the selection, clear the composition display
                if (onLoadComposition) {
                  onLoadComposition(null);
                }
              }
            }}
            disabled={isLoadingCompositions}
            style={{
              flex: '1',
              padding: '8px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: isLoadingCompositions ? '#f5f5f5' : 'white'
            }}
          >
            <option value="">-- Select a saved composition --</option>
            {savedCompositions.map(comp => (
              <option key={comp.id} value={comp.id}>
                {comp.title} ({comp.style}) - {new Date(comp.createdAt).toLocaleDateString()}
              </option>
            ))}
          </select>
          {isLoadingCompositions && (
            <span style={{ fontSize: '12px', color: '#666' }}>Loading...</span>
          )}
          {savedCompositions.length === 0 && !isLoadingCompositions && (
            <span style={{ fontSize: '12px', color: '#999', fontStyle: 'italic' }}>No saved compositions</span>
          )}
        </div>
      </div>
      
      {/* Composition Settings */}
      <div className="composition-settings" style={{ marginBottom: '25px' }}>
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '15px' }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
              Composition Title:
            </label>
            <input
              type="text"
              value={compositionTitle}
              onChange={(e) => setCompositionTitle(e.target.value)}
              placeholder="e.g., A Day at School"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>
          
          <div style={{ minWidth: '150px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
              Word Length:
            </label>
            <input
              type="number"
              value={wordLength}
              onChange={(e) => setWordLength(Math.max(100, parseInt(e.target.value) || 300))}
              min="100"
              max="2000"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>
          
          <div style={{ minWidth: '150px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
              Style:
            </label>
            <select
              value={style}
              onChange={(e) => setStyle(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              {styles.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Nodes Section */}
      <div className="nodes-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3 style={{ margin: 0, color: '#333' }}>Nodes ({nodes.length}):</h3>
          <button
            onClick={addNode}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold'
            }}
          >
            + Add Node
          </button>
        </div>

        {nodes.map((node, nodeIndex) => (
          <div
            key={node.id}
            style={{
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: 'white',
              border: '2px solid #e0e0e0',
              borderRadius: '6px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ flex: 1, marginRight: '10px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#555' }}>
                  Node {nodeIndex + 1}:
                </label>
                <input
                  type="text"
                  value={node.name}
                  onChange={(e) => updateNodeName(node.id, e.target.value)}
                  placeholder="e.g., school, student (Peter), teacher (Mr. Chan)"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              {nodes.length > 1 && (
                <button
                  onClick={() => removeNode(node.id)}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    marginTop: '25px'
                  }}
                >
                  ✕ Remove
                </button>
              )}
            </div>

            {/* Step Selection for Node */}
            <div style={{ marginTop: '15px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: '#555', fontSize: '13px' }}>
                Selected Steps ({node.selectedSteps.size}/15):
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {DEFAULT_STEP_LABELS.map((label, stepIndex) => {
                  const isSelected = node.selectedSteps.has(stepIndex);
                  return (
                    <button
                      key={stepIndex}
                      onClick={() => toggleNodeStep(node.id, stepIndex)}
                      disabled={isSelected && node.selectedSteps.size === 1}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: isSelected ? (stepColors[stepIndex] || '#4CAF50') : '#e0e0e0',
                        color: isSelected ? 'white' : '#666',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: (isSelected && node.selectedSteps.size === 1) ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        fontWeight: isSelected ? 'bold' : 'normal',
                        opacity: (isSelected && node.selectedSteps.size === 1) ? 0.6 : 1
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Start AI Expansion Button for Node */}
            <div style={{ marginTop: '15px', textAlign: 'center' }}>
              <button
                onClick={() => handleNodeExpand(node.id)}
                disabled={node.isExpanding || !node.name.trim()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: node.expandedData ? '#28a745' : (node.isExpanding ? '#6c757d' : '#17a2b8'),
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (node.isExpanding || !node.name.trim()) ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: 'bold'
                }}
              >
                {node.isExpanding ? '⏳ Expanding...' : node.expandedData ? '✓ Expanded' : '✨ Start AI Expansion'}
              </button>
              {node.expansionError && (
                <div style={{ marginTop: '8px', padding: '6px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', fontSize: '12px' }}>
                  Error: {node.expansionError}
                </div>
              )}
            </div>

            {/* Display Expanded Steps for Node */}
            {node.expandedData && (
              <div style={{ marginTop: '15px', padding: '12px', backgroundColor: '#e8f5e9', borderRadius: '6px', border: '1px solid #4caf50' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#2e7d32', fontSize: '14px' }}>
                  ✓ Expanded: {node.expandedData.topic}
                </h4>
                <div style={{ marginBottom: '8px', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                  Select one or more sentences below ({node.selectedSentenceIndices.size} selected):
                </div>
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {node.expandedData.steps.map((step, stepIndex) => {
                    // Find the original index from selectedSteps to get correct color
                    const selectedStepsArray = Array.from(node.selectedSteps).sort((a, b) => a - b);
                    const originalIndex = selectedStepsArray[stepIndex];
                    const isSelected = node.selectedSentenceIndices.has(stepIndex);
                    return (
                      <div 
                        key={stepIndex} 
                        onClick={() => handleSentenceSelect(node.id, stepIndex)}
                        style={{ 
                          marginBottom: '8px', 
                          padding: '10px', 
                          backgroundColor: isSelected ? '#fff3cd' : 'white',
                          border: isSelected ? '2px solid #ffc107' : '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: isSelected ? '0 2px 4px rgba(255, 193, 7, 0.3)' : 'none'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                            e.currentTarget.style.borderColor = '#ffc107';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = 'white';
                            e.currentTarget.style.borderColor = '#ddd';
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                          {isSelected && (
                            <span style={{ 
                              marginRight: '8px', 
                              color: '#ffc107', 
                              fontSize: '16px',
                              fontWeight: 'bold'
                            }}>
                              ✓
                            </span>
                          )}
                          <strong style={{ color: stepColors[originalIndex] || '#333' }}>
                            {step.label}:
                          </strong>
                        </div>
                        <div style={{ paddingLeft: isSelected ? '24px' : '0' }}>
                          {step.content}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Compose Button - Only enabled when at least one sentence is selected */}
      <div style={{ marginTop: '25px', textAlign: 'center' }}>
        <div style={{ marginBottom: '10px', fontSize: '13px', color: hasSelectedSentences ? '#28a745' : '#dc3545' }}>
          {hasSelectedSentences
            ? `✓ Ready to compose! (${totalSelectedSentences} sentence(s) selected)`
            : 'Please select at least one sentence from the expanded nodes to compose.'}
        </div>
        <button
          onClick={handleCompose}
          disabled={isGenerating || !compositionTitle.trim() || !hasSelectedSentences}
          style={{
            padding: '12px 30px',
            backgroundColor: (isGenerating || !hasSelectedSentences) ? '#6c757d' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: (isGenerating || !hasSelectedSentences) ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            minWidth: '200px'
          }}
        >
          {isGenerating ? 'Compiling...' : '🎨 Start AI Composition'}
        </button>
      </div>
    </div>
  );
};

export default CompositionComposer;

